/**
 * Bulk Operation Details Component
 * Displays detailed information about a bulk operation
 */

import React from 'react';
import './BulkOperationDetails.css';

const BulkOperationDetails = ({ operation, onClose }) => {
  if (!operation) return null;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return { text: 'Completed', class: 'completed', color: '#38a169' };
      case 'in_progress':
        return { text: 'In Progress', class: 'in_progress', color: '#d69e2e' };
      case 'failed':
        return { text: 'Failed', class: 'failed', color: '#e53e3e' };
      case 'pending':
        return { text: 'Pending', class: 'pending', color: '#718096' };
      default:
        return { text: 'Unknown', class: 'unknown', color: '#718096' };
    }
  };

  const statusBadge = getStatusBadge(operation.status);

  return (
    <div className="bulk-operation-details-overlay">
      <div className="bulk-operation-details-modal">
        <div className="bulk-operation-details-header">
          <h3>Bulk Operation Details</h3>
          <button 
            className="close-button"
            onClick={onClose}
            aria-label="Close details"
          >
            ×
          </button>
        </div>

        <div className="bulk-operation-details-content">
          <div className="details-section">
            <h4>Operation Information</h4>
            <div className="details-grid">
              <div className="detail-item">
                <label>Operation Type:</label>
                <span>{operation.operation_type || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <label>Status:</label>
                <span 
                  className={`status-badge ${statusBadge.class}`}
                  style={{ color: statusBadge.color }}
                >
                  {statusBadge.text}
                </span>
              </div>
              <div className="detail-item">
                <label>Created:</label>
                <span>
                  {operation.created_at ? 
                    new Date(operation.created_at).toLocaleString() : 'N/A'
                  }
                </span>
              </div>
              <div className="detail-item">
                <label>Updated:</label>
                <span>
                  {operation.updated_at ? 
                    new Date(operation.updated_at).toLocaleString() : 'N/A'
                  }
                </span>
              </div>
            </div>
          </div>

          {operation.progress && (
            <div className="details-section">
              <h4>Progress</h4>
              <div className="progress-info">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${operation.progress.percentage || 0}%` }}
                  ></div>
                </div>
                <div className="progress-text">
                  {operation.progress.completed || 0} of {operation.progress.total || 0} items processed
                  ({operation.progress.percentage || 0}%)
                </div>
              </div>
            </div>
          )}

          {operation.results && (
            <div className="details-section">
              <h4>Results</h4>
              <div className="results-grid">
                <div className="result-item success">
                  <span className="result-count">{operation.results.successful || 0}</span>
                  <span className="result-label">Successful</span>
                </div>
                <div className="result-item failed">
                  <span className="result-count">{operation.results.failed || 0}</span>
                  <span className="result-label">Failed</span>
                </div>
                <div className="result-item skipped">
                  <span className="result-count">{operation.results.skipped || 0}</span>
                  <span className="result-label">Skipped</span>
                </div>
              </div>
            </div>
          )}

          {operation.error_message && (
            <div className="details-section">
              <h4>Error Details</h4>
              <div className="error-message">
                {operation.error_message}
              </div>
            </div>
          )}

          {operation.parameters && (
            <div className="details-section">
              <h4>Parameters</h4>
              <div className="parameters-list">
                {Object.entries(operation.parameters).map(([key, value]) => (
                  <div key={key} className="parameter-item">
                    <label>{key}:</label>
                    <span>{JSON.stringify(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bulk-operation-details-footer">
          <button 
            className="btn btn-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkOperationDetails;
